// Catálogo de productos — solo consulta: SureKeep es copia de Zentral y no da de alta ni edita.
// Productos del negocio, fila por SKU de una tienda, piezas por tienda y ficha por IMEI o serie.
import { useMemo, useState, type ReactNode, type SyntheticEvent } from 'react';
import { Package } from 'lucide-react';
import { Button, EmptyState, Input, Loading, Modal, Select, Tabs, type SelectOption } from '@/kit';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/api/errors';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { OneLineTags } from '@/shared/patterns/OneLineTags';
import { ProductStatusEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductStatusEnum';
import { ProductTierEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductTierEnum';
import { InventoryItemStatusEnum } from '@fiado/type-kit/bin/retailCatalog/enums/InventoryItemStatusEnum';
import { ReconciliationEntityEnum } from '@fiado/type-kit/bin/retailIntegration/enums/ReconciliationEntityEnum';
import { ReconcileButton } from '../admin/reconciliation/components/ReconcileButton';
import {
    EntityDetailModal,
    ListToolbar,
    SearchInput,
    SectionHeader,
    StatusBadge,
    SummaryStats,
    type StatusConfig,
} from '../components';
import { useRetailers, useStores } from '../negocios/api/useRetailOrg';
import {
    useInventoryByProduct,
    useInventoryItem,
    useProducts,
    useRefreshProducts,
    useStoreInventory,
    type CatalogProduct,
} from './api/useRetailCatalog';
import {
    CATEGORY_LABEL,
    TRACKING_LABEL,
    formatComponents,
    tracksPieces,
} from './lib/catalogLabels';
import { formatStorage } from '@/shared/lib/format/capacity';

const PRODUCT_STATUS: Record<string, StatusConfig> = {
    [ProductStatusEnum.ACTIVE]: { label: 'Activo', variant: 'success' },
    [ProductStatusEnum.INACTIVE]: { label: 'Inactivo', variant: 'secondary' },
    [ProductStatusEnum.DISCONTINUED]: { label: 'Descontinuado', variant: 'warning' },
};
const TIER_LABEL: Record<string, string> = {
    [ProductTierEnum.LOW]: 'Baja',
    [ProductTierEnum.MID]: 'Media',
    [ProductTierEnum.HIGH]: 'Alta',
    [ProductTierEnum.PREMIUM]: 'Premium',
};
const INV_STATUS: Record<string, StatusConfig> = {
    [InventoryItemStatusEnum.AVAILABLE]: { label: 'Disponible', variant: 'success' },
    [InventoryItemStatusEnum.RESERVED]: { label: 'Reservado', variant: 'info' },
    [InventoryItemStatusEnum.SOLD]: { label: 'Vendido', variant: 'secondary' },
    [InventoryItemStatusEnum.RETURNED]: { label: 'Devuelto', variant: 'warning' },
    [InventoryItemStatusEnum.DAMAGED]: { label: 'Dañado', variant: 'destructive' },
    [InventoryItemStatusEnum.HELD]: { label: 'Apartada en venta', variant: 'info' },
    [InventoryItemStatusEnum.IN_TRANSIT]: { label: 'En tránsito', variant: 'warning' },
};

/** Sin precio registrado se pinta «—», nunca $0. */
const mxn = (cents: number | null | undefined): string =>
    cents == null
        ? '—'
        : (cents / 100).toLocaleString('es-MX', {
              style: 'currency',
              currency: 'MXN',
              minimumFractionDigits: 0,
          });

const errMsg = (e: unknown, fallback: string): string =>
    e instanceof Error ? e.message : fallback;

const tierLabel = (tier: string | null): string => (tier ? (TIER_LABEL[tier] ?? tier) : 'Sin gama');

const yesNo = (v: boolean | undefined): string => (v === undefined ? '—' : v ? 'Sí' : 'No');

const TABS = [
    { key: 'productos', label: 'Productos' },
    { key: 'tienda', label: 'Inventario por tienda' },
    { key: 'imei', label: 'Consulta por IMEI' },
] as const;

export function CatalogoPage() {
    const [retailerId, setRetailerId] = useState('');
    const [tab, setTab] = useState<string>('productos');
    const { data: retailers, isLoading: loadingRetailers } = useRetailers();

    const retailerOptions: SelectOption[] = (retailers ?? []).map((r) => ({
        value: r.retailerId,
        label: r.name,
    }));

    return (
        <div className="space-y-4">
            <SectionHeader title="CATÁLOGO" divider />

            <div className="flex items-end gap-3">
                <div className="w-full max-w-xs">
                    {loadingRetailers ? (
                        <Loading text="Cargando negocios…" />
                    ) : (
                        <Select
                            label="Negocio"
                            value={retailerId}
                            onChange={setRetailerId}
                            options={retailerOptions}
                            placeholder="Selecciona un negocio…"
                        />
                    )}
                </div>
                {retailerId ? <CatalogReconcile retailerId={retailerId} /> : null}
            </div>

            {!retailerId ? (
                <EmptyState
                    icon={<Package className="size-8" aria-hidden />}
                    title="Elige un negocio"
                    description="Selecciona un negocio para consultar su catálogo e inventario."
                />
            ) : (
                <>
                    <Tabs tabs={TABS} activeTab={tab} onChange={setTab} />
                    {tab === 'productos' ? <ProductsTab retailerId={retailerId} /> : null}
                    {tab === 'tienda' ? <StoreInventoryTab retailerId={retailerId} /> : null}
                    {tab === 'imei' ? <ItemLookupTab retailerId={retailerId} /> : null}
                </>
            )}
        </div>
    );
}

const CATALOG_TARGET = { entity: ReconciliationEntityEnum.CATALOG } as const;

/** «Actualizar» del catálogo del negocio elegido; al terminar vuelve a pedir los productos. */
function CatalogReconcile({ retailerId }: { retailerId: string }) {
    const refreshProducts = useRefreshProducts(retailerId);
    return (
        <ReconcileButton
            retailerId={retailerId}
            target={CATALOG_TARGET}
            onComplete={refreshProducts}
        />
    );
}

// ── Productos ────────────────────────────────────────────────────────────

function productRecord(p: CatalogProduct): Record<string, unknown> {
    return {
        SKU: p.sku,
        Producto: itemDisplayName(p),
        Capacidad: formatStorage(p.capacity) || null,
        Color: p.color || null,
        Categoría: p.category ? CATEGORY_LABEL[p.category] : null,
        Rastreo: p.trackingMode ? TRACKING_LABEL[p.trackingMode] : null,
        Gama: tierLabel(p.tier),
        Precio: mxn(p.basePriceCents),
        'Se vende suelto': yesNo(p.sellable),
        'Visible en la venta': yesNo(p.showInSalesFlow),
        Componentes: formatComponents(p.components) || null,
        Estatus: (PRODUCT_STATUS[p.status] as StatusConfig | undefined)?.label ?? p.status,
    };
}

/** Busca sin distinguir mayúsculas en marca, modelo, descripción y SKU; tolera campos ausentes. */
function matchesSearch(p: CatalogProduct, query: string): boolean {
    return [p.brand, p.model, p.description, p.sku].some((field) =>
        field?.toLowerCase().includes(query),
    );
}

function ProductsTab({ retailerId }: { retailerId: string }) {
    const [search, setSearch] = useState('');
    const [viewProduct, setViewProduct] = useState<CatalogProduct | null>(null);
    const [piecesSku, setPiecesSku] = useState<string | null>(null);
    const { data, isLoading, isError, error } = useProducts(retailerId);

    const products = useMemo(() => data ?? [], [data]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return products;
        return products.filter((p) => matchesSearch(p, q));
    }, [products, search]);

    const activeCount = products.filter((p) => p.status === ProductStatusEnum.ACTIVE).length;

    return (
        <div className="space-y-3">
            <ListToolbar
                search={
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        placeholder="Buscar por marca, modelo, descripción o SKU…"
                    />
                }
            />

            {isLoading ? (
                <Loading text="Cargando productos…" />
            ) : isError ? (
                <EmptyState
                    title="No se pudo cargar el catálogo"
                    description={errMsg(error, 'Error')}
                />
            ) : filtered.length === 0 ? (
                <EmptyState
                    title="Sin productos"
                    description="Los productos llegan de la copia de Zentral."
                />
            ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Producto</TableHead>
                                <TableHead>SKU</TableHead>
                                <TableHead>Categoría</TableHead>
                                <TableHead>Rastreo</TableHead>
                                <TableHead>Precio</TableHead>
                                <TableHead>Gama</TableHead>
                                <TableHead>Financieras</TableHead>
                                <TableHead>Estatus</TableHead>
                                <TableHead />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map((p) => (
                                <TableRow key={p.sku}>
                                    <TableCell className="font-medium">
                                        {itemDisplayName(p)}
                                    </TableCell>
                                    <TableCell className="font-mono text-[12px]">{p.sku}</TableCell>
                                    <TableCell>
                                        {p.category ? CATEGORY_LABEL[p.category] : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {p.trackingMode ? TRACKING_LABEL[p.trackingMode] : '—'}
                                    </TableCell>
                                    <TableCell className="tabular-nums">
                                        {mxn(p.basePriceCents)}
                                    </TableCell>
                                    <TableCell>{tierLabel(p.tier)}</TableCell>
                                    <TableCell>
                                        <OneLineTags items={p.eligibleFinanciers} />
                                    </TableCell>
                                    <TableCell>
                                        <StatusBadge value={p.status} config={PRODUCT_STATUS} />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    setViewProduct(p);
                                                }}
                                            >
                                                Ver
                                            </Button>
                                            {tracksPieces(p) ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        setPiecesSku(p.sku);
                                                    }}
                                                >
                                                    Piezas
                                                </Button>
                                            ) : null}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <SummaryStats
                stats={[
                    { label: 'Productos', value: products.length },
                    { label: 'Activos', value: activeCount },
                ]}
            />

            <EntityDetailModal
                title="Detalle del producto"
                record={viewProduct ? productRecord(viewProduct) : null}
                onClose={() => {
                    setViewProduct(null);
                }}
            />
            <ProductPiecesModal
                retailerId={retailerId}
                sku={piecesSku}
                onClose={() => {
                    setPiecesSku(null);
                }}
            />
        </div>
    );
}

function ProductPiecesModal({
    retailerId,
    sku,
    onClose,
}: {
    retailerId: string;
    sku: string | null;
    onClose: () => void;
}) {
    const { data, isLoading, isError, error } = useInventoryByProduct(retailerId, sku);
    const { data: stores } = useStores(retailerId);
    const storeName = (storeId: string) =>
        stores?.find((s) => s.storeId === storeId)?.name ?? storeId;
    const byStore = (data ?? []).filter((s) => s.pieces.length > 0);

    return (
        <Modal isOpen={!!sku} onClose={onClose} title={`Piezas de ${sku ?? ''}`} size="md">
            {isLoading ? (
                <Loading text="Cargando piezas…" />
            ) : isError ? (
                <EmptyState
                    title="No se pudieron cargar las piezas"
                    description={errMsg(error, 'Error')}
                />
            ) : byStore.length === 0 ? (
                <EmptyState
                    title="Sin piezas"
                    description="Este producto no tiene piezas en ninguna tienda."
                />
            ) : (
                <div className="space-y-4">
                    {byStore.map((store) => (
                        <div key={store.storeId} className="space-y-1.5">
                            <p className="text-sm font-medium">{storeName(store.storeId)}</p>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>IMEI o serie</TableHead>
                                        <TableHead>Estatus</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {store.pieces.map((piece) => (
                                        <TableRow key={piece.identifier}>
                                            <TableCell className="font-mono text-[12px]">
                                                {piece.identifier}
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge
                                                    value={piece.status}
                                                    config={INV_STATUS}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    );
}

// ── Inventario por tienda ────────────────────────────────────────────────

function StoreInventoryTab({ retailerId }: { retailerId: string }) {
    const [storeId, setStoreId] = useState('');
    const { data: stores } = useStores(retailerId);

    const storeOptions: SelectOption[] = (stores ?? []).map((s) => ({
        value: s.storeId,
        label: s.name,
    }));

    return (
        <div className="space-y-3">
            <div className="max-w-xs">
                <Select
                    label="Tienda"
                    value={storeId}
                    onChange={setStoreId}
                    options={storeOptions}
                    placeholder="Selecciona una tienda…"
                />
            </div>
            {storeId ? (
                <StoreInventoryList retailerId={retailerId} storeId={storeId} />
            ) : (
                <EmptyState
                    icon={<Package className="size-8" aria-hidden />}
                    title="Elige una tienda"
                    description="Selecciona una tienda para ver cuánto tiene de cada producto."
                />
            )}
        </div>
    );
}

function StoreInventoryList({ retailerId, storeId }: { retailerId: string; storeId: string }) {
    const { data, isLoading, isError, error } = useStoreInventory(retailerId, storeId);
    const rows = useMemo(() => data ?? [], [data]);
    const availableTotal = rows.reduce((sum, r) => sum + r.available, 0);

    if (isLoading) return <Loading text="Cargando inventario…" />;
    if (isError) {
        return (
            <EmptyState
                title="No se pudo cargar el inventario"
                description={errMsg(error, 'Error')}
            />
        );
    }

    return (
        <div className="space-y-3">
            {rows.length === 0 ? (
                <EmptyState
                    title="Sin inventario"
                    description="Esta tienda no tiene productos con saldo."
                />
            ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Producto</TableHead>
                                <TableHead>SKU</TableHead>
                                <TableHead>Categoría</TableHead>
                                <TableHead>Rastreo</TableHead>
                                <TableHead>Disponible</TableHead>
                                <TableHead>Precio</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.sku}>
                                    <TableCell className="font-medium">
                                        <div>{itemDisplayName(r)}</div>
                                        {r.components?.length ? (
                                            <div className="text-[12px] text-muted-foreground">
                                                {formatComponents(r.components)}
                                            </div>
                                        ) : null}
                                    </TableCell>
                                    <TableCell className="font-mono text-[12px]">{r.sku}</TableCell>
                                    <TableCell>{CATEGORY_LABEL[r.category]}</TableCell>
                                    <TableCell>{TRACKING_LABEL[r.trackingMode]}</TableCell>
                                    <TableCell className="tabular-nums">{r.available}</TableCell>
                                    <TableCell className="tabular-nums">
                                        {mxn(r.basePriceCents)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <SummaryStats
                stats={[
                    { label: 'Productos', value: rows.length },
                    { label: 'Disponibles', value: availableTotal },
                ]}
            />
        </div>
    );
}

// ── Consulta por IMEI ────────────────────────────────────────────────────

function ItemLookupTab({ retailerId }: { retailerId: string }) {
    const [draft, setDraft] = useState('');
    const [identifier, setIdentifier] = useState<string | null>(null);
    const { data, isLoading, isError, error } = useInventoryItem(retailerId, identifier);
    const { data: stores } = useStores(retailerId);
    const { data: products } = useProducts(retailerId);
    const product = data ? products?.find((p) => p.productId === data.productId) : undefined;

    const submit = (e: SyntheticEvent) => {
        e.preventDefault();
        const value = draft.trim();
        setIdentifier(value || null);
    };
    const notFound = error instanceof ApiError && error.httpStatus === 404;

    return (
        <div className="space-y-3">
            <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
                <Input
                    label="IMEI o serie"
                    value={draft}
                    onChange={(e) => {
                        setDraft(e.target.value);
                    }}
                    className="min-w-[18rem]"
                />
                <Button type="submit" disabled={!draft.trim()}>
                    Buscar
                </Button>
            </form>

            {!identifier ? null : isLoading ? (
                <Loading text="Buscando la pieza…" />
            ) : isError ? (
                <EmptyState
                    title={
                        notFound
                            ? 'No encontramos esa pieza en este negocio.'
                            : 'No se pudo consultar la pieza'
                    }
                    description={
                        notFound
                            ? 'Revisa el IMEI o la serie e intenta de nuevo.'
                            : errMsg(error, 'Error')
                    }
                />
            ) : data ? (
                <section
                    aria-label="Ficha de la pieza"
                    className="grid max-w-xl grid-cols-1 gap-x-4 gap-y-2 rounded-lg border border-border p-4 sm:grid-cols-2"
                >
                    <ItemField
                        label="IMEI o serie"
                        value={<span className="font-mono">{data.imei}</span>}
                    />
                    <ItemField
                        label="Estatus"
                        value={<StatusBadge value={data.status} config={INV_STATUS} />}
                    />
                    <ItemField
                        label="Tienda"
                        value={
                            stores?.find((s) => s.storeId === data.storeId)?.name ?? data.storeId
                        }
                    />
                    <ItemField
                        label="Producto"
                        value={
                            product ? (
                                <>
                                    <span>{itemDisplayName(product)}</span>
                                    <span className="block font-mono text-[11px] text-muted-foreground">
                                        {`SKU ${product.sku}`}
                                    </span>
                                </>
                            ) : (
                                '—'
                            )
                        }
                    />
                    <ItemField
                        label="Recibida"
                        value={new Date(data.receivedAt).toLocaleDateString('es-MX')}
                    />
                </section>
            ) : null}
        </div>
    );
}

function ItemField({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="min-w-0 space-y-0.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {label}
            </p>
            <div className="break-words text-[13px] text-foreground">{value}</div>
        </div>
    );
}
